import { useCallback, useId } from "react";
import type { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import { Board } from "@design-system/Board/Board";
import type {
    BoardCollapsedChange,
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { compareWorkRows } from "../workRowCompare";
import {
    type ClientsWorkOverviewCallbacks,
    useWorkRowActions,
} from "../WorkRowActions";
import type { WorkCardFieldColumn } from "./workCardFieldColumns";
import { getWorkCardLabel, WorkBoardCard } from "./WorkBoardCard";

export interface WorkBoardProps {
    rows: readonly ClientsWorkOverviewWorkRow[];
    columns: readonly BoardColumnDef[];
    /** Undefined without swimlanes. */
    lanes: readonly BoardLaneDef[] | undefined;
    getPlacements: (row: ClientsWorkOverviewWorkRow) => BoardPlacement[];
    /** The tags cards show, in order. */
    fieldColumns: readonly WorkCardFieldColumn[];
    /** Off when the columns already name the client. */
    isClientShown: boolean;
    /** The view's sort; cards follow it within each column, as List's rows do. */
    sort: ClientsWorkOverviewSortValue | undefined;
    collapsedColumnIds: readonly string[];
    collapsedLaneIds: readonly string[];
    onCollapsedChange: (next: BoardCollapsedChange) => void;
    /** Row ids, shared with List's checkboxes; every copy of a card shows selected. */
    selectedItemIds: ReadonlySet<string>;
    onSelectionChange: (itemIds: ReadonlySet<string>) => void;
    /** Cards a live update just moved, which enter in their new cells. */
    enteringItemIds: readonly string[];
    /** List's row action callbacks, for the cards' context menus. */
    callbacks: ClientsWorkOverviewCallbacks | undefined;
    /** The row id of the card whose details are open, if any. */
    activeItemId: string | null;
    /** Opens a card's details beside the board. */
    onOpenItem: (row: ClientsWorkOverviewWorkRow) => void;
}

const getRowId = (row: ClientsWorkOverviewWorkRow) => row.id;
const getRowLabel = (row: ClientsWorkOverviewWorkRow) =>
    getWorkCardLabel({ row, isClientShown: false });

/**
 * The Work page's board: one card per application, whose details open
 * beside the board on click or Enter, with List's row actions on
 * right-click and multi-select.
 * The context menus share one confirmation modal, so cards mount none.
 */
export const WorkBoard = ({
    rows,
    columns,
    lanes,
    getPlacements,
    fieldColumns,
    isClientShown,
    sort,
    collapsedColumnIds,
    collapsedLaneIds,
    onCollapsedChange,
    selectedItemIds,
    onSelectionChange,
    enteringItemIds,
    callbacks,
    activeItemId,
    onOpenItem,
}: Readonly<WorkBoardProps>) => {
    const actionsDescriptionId = useId();
    const { onConfirmationRequest, confirmationModal } = useWorkRowActions();

    const compareItems = useCallback(
        (
            first: ClientsWorkOverviewWorkRow,
            second: ClientsWorkOverviewWorkRow,
        ) => compareWorkRows(first, second, sort),
        [sort],
    );

    const renderCard = useCallback(
        (row: ClientsWorkOverviewWorkRow) => (
            <WorkBoardCard
                row={row}
                fieldColumns={fieldColumns}
                isClientShown={isClientShown}
                isActive={row.id === activeItemId}
                onOpen={onOpenItem}
                callbacks={callbacks}
                onConfirmationRequest={onConfirmationRequest}
                actionsDescriptionId={actionsDescriptionId}
            />
        ),
        [
            fieldColumns,
            isClientShown,
            activeItemId,
            onOpenItem,
            callbacks,
            onConfirmationRequest,
            actionsDescriptionId,
        ],
    );

    return (
        <>
            <div id={actionsDescriptionId} hidden>
                Press Shift+F10 for actions
            </div>
            <Board
                aria-label="Work"
                items={rows}
                columns={columns}
                lanes={lanes}
                hasCommandIsland
                getItemId={getRowId}
                getItemLabel={getRowLabel}
                getPlacements={getPlacements}
                renderCard={renderCard}
                compareItems={compareItems}
                emptyColumnLabel="No work items"
                collapsedColumnIds={collapsedColumnIds}
                collapsedLaneIds={collapsedLaneIds}
                onCollapsedChange={onCollapsedChange}
                selectedItemIds={selectedItemIds}
                onSelectionChange={onSelectionChange}
                enteringItemIds={enteringItemIds}
                enteringReveal="none"
            />
            {confirmationModal}
        </>
    );
};
